import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import DeleteUserModal from './DeleteUserModal';
import './AdminUserList.css';

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  is_admin: boolean;
  strava_connected: boolean;
  club: boolean;
  is_trainer: boolean;
  last_login: string | null;
  admin_tags: string[];
  created_at: string | null;
  account_status: string;
}

type SortField = 'id' | 'username' | 'first_name' | 'last_name' | 'email' | 'is_admin' | 'strava_connected' | 'last_login' | 'admin_tags';
type SortDir = 'asc' | 'desc';
type StatusFilter = '' | 'admin' | 'strava' | 'no_login';

const AdminUserList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<number | null>(null);
  const [tokenUsage, setTokenUsage] = useState<Record<number, {total_tokens: number; request_count: number; cost_usd: number; input_tokens: number; cached_input_tokens: number; output_tokens: number}>>({});
  const [totalCost, setTotalCost] = useState(0);

  // Filters
  const [textFilter, setTextFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');

  // Sort
  const [sortField, setSortField] = useState<SortField>('id');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Tags
  const [editingTagUserId, setEditingTagUserId] = useState<number | null>(null);
  const [newTagValue, setNewTagValue] = useState('');

  // Delete
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; user: User | null }>({ isOpen: false, user: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const [response, tokenRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/admin/users`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/admin/users/token-usage`, { credentials: 'include' }),
        ]);
        if (response.ok) {
          const data = await response.json();
          setUsers(data.users);
        } else {
          setError('Błąd pobierania użytkowników');
        }
        if (tokenRes.ok) {
          const data = await tokenRes.json();
          setTokenUsage(data.usage);
          setTotalCost(data.total_cost_usd || 0);
        }
      } catch (err) {
        setError('Błąd sieci');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // All unique tags for dropdown
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    users.forEach(u => (u.admin_tags || []).forEach(t => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [users]);

  // Filtering
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      // Text filter
      if (textFilter) {
        const q = textFilter.toLowerCase();
        const matches = u.username.toLowerCase().includes(q)
          || u.first_name.toLowerCase().includes(q)
          || u.last_name.toLowerCase().includes(q)
          || u.email.toLowerCase().includes(q)
          || String(u.id).includes(q);
        if (!matches) return false;
      }
      // Tag filter
      if (tagFilter) {
        if (!(u.admin_tags || []).includes(tagFilter)) return false;
      }
      // Status filter
      if (statusFilter === 'admin' && !u.is_admin) return false;
      if (statusFilter === 'strava' && !u.strava_connected) return false;
      if (statusFilter === 'no_login' && u.last_login) return false;
      return true;
    });
  }, [users, textFilter, tagFilter, statusFilter]);

  // Sorting
  const sortedUsers = useMemo(() => {
    const sorted = [...filteredUsers];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'id':
          cmp = a.id - b.id;
          break;
        case 'username':
          cmp = a.username.localeCompare(b.username);
          break;
        case 'first_name':
          cmp = a.first_name.localeCompare(b.first_name);
          break;
        case 'last_name':
          cmp = a.last_name.localeCompare(b.last_name);
          break;
        case 'email':
          cmp = a.email.localeCompare(b.email);
          break;
        case 'is_admin':
          cmp = (a.is_admin ? 1 : 0) - (b.is_admin ? 1 : 0);
          break;
        case 'strava_connected':
          cmp = (a.strava_connected ? 1 : 0) - (b.strava_connected ? 1 : 0);
          break;
        case 'last_login': {
          const aDate = a.last_login ? new Date(a.last_login).getTime() : 0;
          const bDate = b.last_login ? new Date(b.last_login).getTime() : 0;
          cmp = aDate - bDate;
          break;
        }
        case 'admin_tags':
          cmp = (a.admin_tags || []).length - (b.admin_tags || []).length;
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredUsers, sortField, sortDir]);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }, [sortField]);

  const sortIcon = (field: SortField) => {
    if (sortField !== field) return ' \u2195';
    return sortDir === 'asc' ? ' \u2191' : ' \u2193';
  };

  // Admin toggle
  const handleToggleAdmin = async (userId: number, currentIsAdmin: boolean) => {
    setUpdating(userId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/admin`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_admin: !currentIsAdmin })
      });
      if (response.ok) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_admin: !currentIsAdmin } : u));
      }
    } catch (err) {
      // silent
    } finally {
      setUpdating(null);
    }
  };

  // Tag management
  const handleAddTag = async (userId: number) => {
    const tag = newTagValue.trim();
    if (!tag) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/tags`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ add_tag: tag })
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, admin_tags: data.admin_tags } : u));
        setNewTagValue('');
      }
    } catch (err) {
      // silent
    }
  };

  const handleRemoveTag = async (userId: number, tag: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/tags`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ remove_tag: tag })
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, admin_tags: data.admin_tags } : u));
      }
    } catch (err) {
      // silent
    }
  };

  // Delete
  const handleDeleteUser = (user: User) => {
    setDeleteModal({ isOpen: true, user });
  };

  const handleConfirmDelete = async (confirmedEmail: string) => {
    if (!deleteModal.user) return;
    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${deleteModal.user.id}/delete`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirmed_email: confirmedEmail })
      });
      if (response.ok) {
        setUsers(prev => prev.filter(u => u.id !== deleteModal.user!.id));
        setDeleteModal({ isOpen: false, user: null });
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to delete user');
      }
    } catch (error) {
      alert('Network error occurred');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div>Ładowanie użytkowników...</div>;
  if (error) return <div className="card" style={{ color: 'red' }}>{error}</div>;

  return (
    <div className="card admin-users">
      <div className="admin-users-header">
        <h2>Użytkownicy ({filteredUsers.length}/{users.length})</h2>
        {totalCost > 0 && (
          <span style={{ fontSize: '13px', color: '#666', marginLeft: '12px' }}>
            AI total: <strong>${totalCost.toFixed(2)}</strong>
          </span>
        )}
      </div>

      <div className="admin-users-filters">
        <input
          className="admin-filter-input"
          type="text"
          placeholder="Szukaj (ID, username, imię, email)..."
          value={textFilter}
          onChange={e => setTextFilter(e.target.value)}
        />
        <select
          className="admin-filter-select"
          value={tagFilter}
          onChange={e => setTagFilter(e.target.value)}
        >
          <option value="">Wszystkie tagi</option>
          {allTags.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          className="admin-filter-select"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as StatusFilter)}
        >
          <option value="">Wszystkie statusy</option>
          <option value="admin">Tylko Admin</option>
          <option value="strava">Tylko Strava</option>
          <option value="no_login">Brak logowania</option>
        </select>
        {(textFilter || tagFilter || statusFilter) && (
          <button
            className="admin-filter-clear"
            onClick={() => { setTextFilter(''); setTagFilter(''); setStatusFilter(''); }}
          >
            Wyczyść filtry
          </button>
        )}
      </div>

      <div className="admin-users-table-wrap">
        <table className="admin-users-table">
          <thead>
            <tr>
              <th className="col-id" onClick={() => handleSort('id')}>ID{sortIcon('id')}</th>
              <th className="col-username" onClick={() => handleSort('username')}>Username{sortIcon('username')}</th>
              <th onClick={() => handleSort('first_name')}>Imię{sortIcon('first_name')}</th>
              <th onClick={() => handleSort('last_name')}>Nazwisko{sortIcon('last_name')}</th>
              <th onClick={() => handleSort('email')}>Email{sortIcon('email')}</th>
              <th className="col-flag" onClick={() => handleSort('is_admin')}>Admin{sortIcon('is_admin')}</th>
              <th className="col-flag" onClick={() => handleSort('strava_connected')}>Strava{sortIcon('strava_connected')}</th>
              <th onClick={() => handleSort('last_login')}>Ostatnie login{sortIcon('last_login')}</th>
              <th className="col-tags" onClick={() => handleSort('admin_tags')}>Tagi{sortIcon('admin_tags')}</th>
              <th className="col-tokens">AI Cost</th>
              <th className="col-actions">Akcje</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(u => (
              <tr key={u.id}>
                <td className="col-id">{u.id}</td>
                <td className="col-username">{u.username}</td>
                <td>{u.first_name}</td>
                <td>{u.last_name}</td>
                <td className="col-email">{u.email}</td>
                <td className="col-flag">
                  {u.is_admin ? <span className="flag-yes">TAK</span> : <span className="flag-no">—</span>}
                </td>
                <td className="col-flag">
                  {u.strava_connected ? <span className="flag-yes">TAK</span> : <span className="flag-no">—</span>}
                </td>
                <td>
                  {u.last_login
                    ? new Date(u.last_login).toLocaleDateString('pl-PL')
                    : <span className="flag-no">brak</span>}
                </td>
                <td className="col-tags">
                  <div className="tags-cell">
                    {(u.admin_tags || []).map(tag => (
                      <span key={tag} className="tag-pill">
                        {tag}
                        <button
                          className="tag-remove"
                          onClick={() => handleRemoveTag(u.id, tag)}
                          title="Usuń tag"
                        >&times;</button>
                      </span>
                    ))}
                    {editingTagUserId === u.id ? (
                      <span className="tag-input-wrap">
                        <input
                          className="tag-input"
                          type="text"
                          value={newTagValue}
                          onChange={e => setNewTagValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { handleAddTag(u.id); }
                            if (e.key === 'Escape') { setEditingTagUserId(null); setNewTagValue(''); }
                          }}
                          placeholder="nowy tag..."
                          autoFocus
                        />
                        <button className="tag-confirm" onClick={() => handleAddTag(u.id)}>+</button>
                        <button className="tag-cancel" onClick={() => { setEditingTagUserId(null); setNewTagValue(''); }}>&times;</button>
                      </span>
                    ) : (
                      <button
                        className="tag-add-btn"
                        onClick={() => { setEditingTagUserId(u.id); setNewTagValue(''); }}
                        title="Dodaj tag"
                      >+</button>
                    )}
                  </div>
                </td>
                <td className="col-tokens">
                  {tokenUsage[u.id] ? (
                    <div style={{fontSize: '0.75rem', lineHeight: 1.4}}>
                      <div style={{fontWeight: 600}}>${tokenUsage[u.id].cost_usd.toFixed(2)} <span style={{color: '#888', fontWeight: 400}}>({tokenUsage[u.id].request_count} req)</span></div>
                      <div style={{color: '#666'}}>in: {(tokenUsage[u.id].input_tokens/1000).toFixed(1)}k · cache: {(tokenUsage[u.id].cached_input_tokens/1000).toFixed(1)}k · out: {(tokenUsage[u.id].output_tokens/1000).toFixed(1)}k</div>
                    </div>
                  ) : '—'}
                </td>
                <td className="col-actions">
                  <div className="actions-cell">
                    <button
                      onClick={() => handleToggleAdmin(u.id, u.is_admin)}
                      disabled={updating === u.id}
                      className={`btn-sm ${u.is_admin ? 'btn-revoke' : 'btn-grant'}`}
                      title={u.is_admin ? 'Odbierz admina' : 'Nadaj admina'}
                    >
                      {u.is_admin ? 'Odbierz' : 'Nadaj'}
                    </button>
                    <button
                      onClick={() => handleDeleteUser(u)}
                      className="btn-sm btn-delete"
                      title="Usuń użytkownika"
                    >
                      Usuń
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {sortedUsers.length === 0 && (
              <tr>
                <td colSpan={11} className="table-empty">Brak wyników</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {deleteModal.user && (
        <DeleteUserModal
          isOpen={deleteModal.isOpen}
          onClose={() => setDeleteModal({ isOpen: false, user: null })}
          onConfirm={handleConfirmDelete}
          user={deleteModal.user}
          loading={deleting}
        />
      )}
    </div>
  );
};

export default AdminUserList;
